import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@evc/platform';
import { BehaviorSubject, Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { ConfigService } from '../config';
import { DownloadReportRequest } from '../report/report-download.interface';
import { UploadReportRequest } from '../report/report-upload.interface';

export interface DriveDownLoadFileInfoRequest {
  fileid: string;
}

export interface DriveDownloadFileInfoResponse {
  filePath: string;
  fileName: string;
  fileExtension: string;
  fileSize: number;
}

export interface DownLoadFileRequest {
  fileid: string;
}

export interface DriveDownLoadFileRequest {
  fileid: string;
  fileExtension: string;
}

export interface DriveDownloadFileResponse {
  filePath: string;
}

export interface DownloadFileResponse {
  filePath: string;
  fileSize: number;
}

export interface DriveUploadFileRequest {
  filePathName: string;
}

export interface StatusResponse {
  status: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class OscCloudService {
  private readonly _statusSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly _baseApiUrl: string = this.getApiUrl();

  public status$: Observable<boolean> = this._statusSubject.asObservable();

  private _headers: HttpHeaders = new HttpHeaders({});

  constructor(
    private readonly _http: HttpClient,
    private readonly _config: ConfigService,
  ) {
    if (this._config.isEvidentConnect) {
      inject(AuthService).accessToken$.pipe(takeUntilDestroyed()).subscribe((token) => {
        this._headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });
      });
    }
  }

  public driveDownloadFileInfo(driveFileDownLoadInfoRequest: DriveDownLoadFileInfoRequest): Observable<DriveDownloadFileInfoResponse> {
    return this._http.post<DriveDownloadFileInfoResponse>(`${this._baseApiUrl}/drivefile/driveDownloadInfo`, driveFileDownLoadInfoRequest, { headers: this._headers });
  }

  public driveDownloadFile(driveFileDownloadRequest: DriveDownLoadFileRequest): Observable<DownloadFileResponse> {
    return this._http.post<DownloadFileResponse>(`${this._baseApiUrl}/drivefile/driveDownload`, driveFileDownloadRequest, { headers: this._headers });
  }

  public driveUploadFile(driveFileUploadRequest: DriveUploadFileRequest): Observable<unknown> {
    return this._http.post<unknown>(`${this._baseApiUrl}/drivefile/driveUpload`, driveFileUploadRequest, { headers: this._headers });
  }

  public downloadReportFile(body: DownloadReportRequest): Observable<Blob> {
    return this._http.post(
      `${this._baseApiUrl}/report/download`,
      this.transformBodyToBlob(body),
      { responseType: 'blob' },
    );
  }

  public uploadReportFile(body: Partial<UploadReportRequest>): Observable<object> {
    return this._http.post(
      `${this._baseApiUrl}/report/upload`,
      this.transformBodyToBlob(body),
      { headers: this._headers },
    );
  }

  public instantStatus(): boolean {
    return this._statusSubject.getValue();
  }

  public setStatus(status: boolean): void {
    this._statusSubject.next(status);
  }

  private getApiUrl(): string {
    if (this._config.isLocal) {
      return 'http://localhost:5000';
    }

    return environment.PROTECTED_URIS?.UNIS_UT ?? '';
  }

  private transformBodyToBlob(body: object): Blob {
    return new Blob([JSON.stringify(body)], { type: 'application/octet-stream' });
  }
}
